import { useState, useEffect, useCallback } from "react";
import { getAuth } from "firebase/auth";
import { Content } from "../utils/types";
import { contentService } from "../services/contentService";
import { useAuthContext } from "../components/AuthContext";

interface UseUnpurchasedContentsReturn {
  unpurchasedContents: Content[];
  loading: boolean;
  finishedFetch: boolean;
  refreshContents: () => Promise<void>;
}

export function useUnpurchasedContents(): UseUnpurchasedContentsReturn {
  const [unpurchasedContents, setUnpurchasedContents] = useState<Content[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [finishedFetch, setFinishedFetch] = useState<boolean>(false);
  const { user } = useAuthContext();
  const auth = getAuth();

  const filterUnpurchasedContents = (
    allContents: Content[],
    purchasedData: { id: string; chapters: number[] }[],
  ): Content[] => {
    return allContents.filter((content) => {
      const purchasedContent = purchasedData.find((pc) => pc.id === content.id);
      const purchasedChapters = purchasedContent?.chapters || [];

      // 購入済みかどうかの判定
      // チャプター1が有料の場合は全チャプター購入済み、無料の場合はチャプター2以降が購入済み
      const isPurchased =
        content.chapters.length > 0 &&
        (content.chapters[0].price > 0
          ? purchasedChapters.length === content.chapters.length
          : purchasedChapters.some((ch) => ch > 1));

      // 購入済みでないコンテンツのみを返す
      return !isPurchased;
    });
  };

  const fetchContentsData = async (uid: string) => {
    try {
      // 全作品を取得
      const allContents = await contentService.fetchAllContents();

      // 購入済み作品情報を取得
      const purchasedData = await contentService.fetchPurchasedContents(uid);

      // 購入済みでないコンテンツのみをフィルタリング
      const unpurchased = filterUnpurchasedContents(allContents, purchasedData);

      setUnpurchasedContents(unpurchased);
    } catch (error) {
      console.error("Failed to fetch unpurchased contents:", error);
      alert("エラーが発生しました。\nしばらくしてから再度お試しください。");
    } finally {
      setLoading(false);
      setFinishedFetch(true);
    }
  };

  const refreshContents = useCallback(async () => {
    if (!user?.uid) return;
    setLoading(true);
    await fetchContentsData(user.uid);
  }, [user?.uid]);

  useEffect(() => {
    const uid = auth.currentUser?.uid;
    if (uid) {
      fetchContentsData(uid);
    } else {
      setLoading(false);
    }
  }, [auth.currentUser?.uid]);

  return {
    unpurchasedContents,
    loading,
    finishedFetch,
    refreshContents,
  };
}
